import React, { useEffect } from 'react';
import {
  FormRow,
  FormWrapper,
  Label,
  SubmitButton,
  CancelButton,
} from '../styles/ComponentStyles';
import {
  StatusSelect,
  BillDateInput,
  BillNumberInput,
  InstallmentSection,
  HelperText,
} from '../styles/BillsFormStyles';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import { BillersDropdown } from './Dropdowns';

const SubscriptionFormModal = ({ editing, initialData, onSubmit, onClose }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    biller_id: '',
    biller: '',
    recurring_type: 'subscription',
    amount: '',
    platform: '',
    store: '',
    remarks: '',
    status: 'active',
    amortization_start_date: '',
    total_months: '',
  });

  useEffect(() => {
    if (editing && initialData) {
      setFormData({
        name: initialData.name || '',
        biller_id: initialData.biller_id || '',
        biller: initialData.biller || '',
        recurring_type: initialData.recurring_type || 'subscription',
        amount: initialData.amount ?? '',
        platform: initialData.platform ?? '',
        store: initialData.store ?? '',
        remarks: initialData.remarks ?? '',
        status: initialData.status || 'active',
        amortization_start_date: initialData.amortization_start_date ?? '',
        total_months: initialData.total_months ?? '',
      });
    } else {
      setFormData({
        name: '',
        biller_id: '',
        biller: '',
        recurring_type: 'subscription',
        amount: '',
        platform: '',
        store: '',
        remarks: '',
        status: 'active',
        amortization_start_date: '',
        total_months: '',
      });
    }
  }, [editing, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      biller_id: formData.biller_id,
      biller: formData.biller,
      recurring_type: formData.recurring_type || 'subscription',
      amount: formData.amount ? parseFloat(formData.amount) : null,
      platform: formData.platform || null,
      store: formData.store || null,
      remarks: formData.remarks || null,
      status: formData.status || 'active',
      amortization_start_date: formData.amortization_start_date || null,
      total_months: formData.total_months ? parseInt(formData.total_months, 10) : null,
    };
    onSubmit(payload);
    onClose();
  };

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{editing ? 'Edit Subscription' : 'Add Subscription'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>

        <FormWrapper onSubmit={handleSubmit}>
          <FormRow>
            <Label htmlFor="name">Name*</Label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name ?? ''}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="biller">Biller (source account)*</Label>
            <BillersDropdown
              value={formData.biller_id}
              onChange={(id, name) => {
                setFormData((prev) => ({ ...prev, biller_id: id, biller: name }));
              }}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="recurring_type">Type</Label>
            <StatusSelect
              id="recurring_type"
              name="recurring_type"
              value={formData.recurring_type || 'subscription'}
              onChange={handleChange}
            >
              <option value="subscription">Subscription</option>
              <option value="installment">Installment</option>
            </StatusSelect>
          </FormRow>
          <FormRow>
            <Label htmlFor="amount">Amount (monthly)</Label>
            <BillNumberInput
              id="amount"
              name="amount"
              value={formData.amount ?? ''}
              onChange={handleChange}
              placeholder="Monthly amount"
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="platform">Platform</Label>
            <input
              id="platform"
              name="platform"
              type="text"
              value={formData.platform ?? ''}
              onChange={handleChange}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="store">Store</Label>
            <input
              id="store"
              name="store"
              type="text"
              value={formData.store ?? ''}
              onChange={handleChange}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="status">Status</Label>
            <StatusSelect
              id="status"
              name="status"
              value={formData.status || 'active'}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </StatusSelect>
          </FormRow>

          <InstallmentSection>
            <FormRow>
              <Label htmlFor="amortization_start_date">Start Date (optional)</Label>
              <HelperText>Helps track months paid</HelperText>
              <BillDateInput
                id="amortization_start_date"
                name="amortization_start_date"
                value={formData.amortization_start_date ?? ''}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="total_months">Total Months (optional)</Label>
              <HelperText>Leave blank for endless subscriptions</HelperText>
              <BillNumberInput
                id="total_months"
                name="total_months"
                min="1"
                value={formData.total_months ?? ''}
                onChange={handleChange}
                placeholder="e.g. 12, 24"
              />
            </FormRow>
          </InstallmentSection>

          <FormRow>
            <Label htmlFor="remarks">Remarks</Label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks ?? ''}
              onChange={handleChange}
              rows={3}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <SubmitButton type="submit">{editing ? 'Save Changes' : 'Add'}</SubmitButton>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
          </div>
        </FormWrapper>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default SubscriptionFormModal;
